import dayjs from 'dayjs'
import { toApiDate } from '../../../helpers/date'
import { FilterCategory } from '../InvoiceByJob/filtersModel'
import { workAreaStatusNames } from '../workAreaStatusModel'

export type ListScope = {
  startDate: string | null
  endDate: string | null
  divisionGroups?: string[]
  workAreaStatuses?: number[]
  jobTypes?: number[]
  search?: string
}

export type BatchScope = {
  startDate?: string
  cutOffDate?: string
  divisionGroups?: string[]
  workAreaStatuses?: number[]
}

export type ScopeLabels = {
  startDate?: string | null
  cutOffDate?: string | null
  divisionGroups?: string[] | null
  workAreaStatuses?: number[] | null
}

export const hasBatchScope = (scope: ScopeLabels): boolean =>
  !!(scope.startDate || scope.cutOffDate || scope.divisionGroups != null || scope.workAreaStatuses != null)

export const selectedDivisionGroups = (divisions?: FilterCategory): string[] | undefined => {
  if (!divisions || divisions.all || divisions.values.length === 0) {
    return undefined
  }
  return divisions.values.filter(x => x.selected).map(x => String(x.value))
}

export const selectedWorkAreaStatuses = (statuses?: FilterCategory): number[] | undefined => {
  if (!statuses || statuses.all || statuses.values.length === 0) {
    return undefined
  }
  return statuses.values.filter(x => x.selected).map(x => Number(x.value))
}

export const selectedJobTypes = (jobTypes?: FilterCategory): number[] | undefined => {
  if (!jobTypes || jobTypes.all || jobTypes.values.length === 0) {
    return undefined
  }
  return jobTypes.values.filter(x => x.selected).map(x => Number(x.value))
}

export const batchScope = (listScope: ListScope): BatchScope => {
  const scope: BatchScope = {}
  if (listScope.startDate && listScope.endDate) {
    scope.startDate = toApiDate(listScope.startDate)
    scope.cutOffDate = toApiDate(listScope.endDate)
  }
  if (listScope.divisionGroups !== undefined) {
    scope.divisionGroups = listScope.divisionGroups
  }
  if (listScope.workAreaStatuses !== undefined) {
    scope.workAreaStatuses = listScope.workAreaStatuses
  }
  return scope
}

const scopeCalendarDate = (value: string) => dayjs(value.slice(0, 10))

export const scopeDateLabel = (scope: ScopeLabels): string => {
  if (scope.startDate && scope.cutOffDate) {
    const start = scopeCalendarDate(scope.startDate)
    const end = scopeCalendarDate(scope.cutOffDate)
    if (start.year() === end.year()) {
      return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
    }
    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`
  }
  if (scope.cutOffDate) {
    return `Through ${scopeCalendarDate(scope.cutOffDate).format('MMM D, YYYY')}`
  }
  if (scope.startDate) {
    return `From ${scopeCalendarDate(scope.startDate).format('MMM D, YYYY')}`
  }
  return 'Through today'
}

export const scopeDivisionsLabel = (scope: ScopeLabels): string => {
  if (scope.divisionGroups == null) {
    return 'All divisions'
  }
  if (scope.divisionGroups.length === 0) {
    return 'No divisions'
  }
  return scope.divisionGroups.join(', ')
}

export const scopeWorkAreaStatusesLabel = (scope: ScopeLabels): string => {
  if (scope.workAreaStatuses == null) {
    return 'All statuses'
  }
  if (scope.workAreaStatuses.length === 0) {
    return 'No statuses'
  }
  return workAreaStatusNames(scope.workAreaStatuses)
}
